import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Flame, Zap, Shield, Search, Menu, Network } from 'lucide-react';
import { useStreak, useProfile } from '../../features/gamification/hooks/useGamification.js';
import { useCompleteDiagrams } from '../../features/diagrams/hooks/useCompleteDiagrams';
import { ThemeSwitch } from './ThemeSwitch';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const navigate = useNavigate();
  const { data: streak } = useStreak();
  const { data: profile } = useProfile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const diagramsQuery = useCompleteDiagrams(searchOpen);
  const results = useMemo(() => {
    return (diagramsQuery.data ?? [])
      .filter((diagram) => {
        const term = searchTerm.trim().toLocaleLowerCase();
        return (
          !term ||
          [diagram.title, ...(diagram.tags ?? [])].some((value) =>
            value?.toLocaleLowerCase().includes(term),
          )
        );
      })
      .slice(0, 10);
  }, [diagramsQuery.data, searchTerm]);

  // Cmd+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (searchOpen) {
      setSearchTerm('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [searchOpen]);

  // Close on Escape
  useEffect(() => {
    if (!searchOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  const handleSelectResult = (id: string) => {
    setSearchOpen(false);
    navigate(`/diagrams/${id}`);
  };

  return (
    <>
      <header
        className="relative flex h-14 items-center justify-between px-4 lg:px-5"
        style={{
          background:
            'linear-gradient(90deg, var(--xds-chrome) 0%, var(--xds-deep) 50%, var(--xds-chrome) 100%)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--xds-bd2)',
        }}
      >
        {/* Gradient bottom border */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, var(--xds-bd) 30%, var(--xds-bd) 50%, var(--xds-bd) 70%, transparent 100%)',
          }}
        />

        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="rounded-xl p-2 text-[var(--xds-fg3)] hover:bg-[var(--xds-gl)] hover:text-[var(--xds-fg2)] transition-all duration-150"
            aria-label="Toggle sidebar"
          >
            <Menu className="h-[18px] w-[18px]" />
          </button>

          <button
            onClick={() => setSearchOpen(true)}
            className="hidden sm:flex items-center gap-2.5 rounded-xl px-3 py-1.5 text-sm text-[var(--xds-fg3)] transition-all duration-200 w-[220px] group"
            style={{
              background: 'var(--xds-gl)',
              border: '1px solid var(--xds-bd2)',
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.border = '1px solid var(--xds-bd)')
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as HTMLButtonElement).style.border = '1px solid var(--xds-bd2)')
            }
          >
            <Search className="h-3.5 w-3.5" />
            <span className="text-xs">Buscar...</span>
            <kbd
              className="ml-auto rounded-md px-1.5 py-0.5 text-[10px] text-[var(--xds-fg3)]"
              style={{ background: 'var(--xds-gl)' }}
            >
              ⌘K
            </kbd>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <ThemeSwitch />

          {/* Stats row — subtle pill */}
          <div
            className="hidden sm:flex items-center gap-2.5 rounded-xl px-3 py-1.5 text-xs"
            style={{
              background: 'var(--xds-gl)',
              border: '1px solid var(--xds-bd2)',
            }}
          >
            <span className="flex items-center gap-1">
              <Flame className="h-3.5 w-3.5 text-xd-streak" />
              <span className="font-semibold text-[var(--xds-fg2)]">
                {streak?.currentStreak ?? 0}
              </span>
            </span>
            <span className="text-[var(--xd-fg-3)]">|</span>
            <span className="flex items-center gap-1">
              <Zap className="h-3.5 w-3.5 text-xd-xp" />
              <span className="font-semibold text-[var(--xds-fg2)]">
                {(profile?.xp ?? 0).toLocaleString()}
              </span>
            </span>
            <span className="hidden md:flex items-center gap-1 text-[var(--xd-fg-3)]">|</span>
            <span className="hidden md:flex items-center gap-1">
              <Shield className="h-3.5 w-3.5 text-xd-info" />
              <span className="font-semibold text-[var(--xds-fg2)]">
                Nivel {profile?.level ?? 1}
              </span>
            </span>
          </div>
        </div>
      </header>

      {searchOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-start justify-center px-4"
            style={{ paddingTop: '15vh' }}
          >
            <div
              className="absolute inset-0"
              style={{ background: 'color-mix(in srgb, var(--xd-forest-950) 70%, transparent)' }}
              onClick={() => setSearchOpen(false)}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Buscar diagramas"
              className="xd-overlay relative z-10 flex w-full max-w-[640px] flex-col overflow-hidden border border-[var(--xd-bd)] bg-[var(--xd-base)]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 border-b border-[var(--xd-bd)] px-4 py-3">
                <Search className="h-5 w-5 shrink-0 text-[var(--xd-fg-3)]" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar diagramas..."
                  className="flex-1 bg-transparent text-sm text-[var(--xd-fg)] placeholder:text-[var(--xd-fg-3)] focus:outline-none"
                />
                <kbd className="rounded bg-[var(--xd-raised)] px-1.5 py-0.5 text-xs text-[var(--xd-fg-3)]">
                  ESC
                </kbd>
              </div>
              <p className="px-4 pt-3 text-xs text-[var(--xd-fg-3)]">
                Busca títulos y etiquetas de diagramas. Se muestran hasta 10 resultados.
              </p>
              <div className="max-h-80 overflow-y-auto py-2">
                {diagramsQuery.isError ? (
                  <div role="alert" className="px-4 py-6 text-center text-sm text-[var(--xd-fg-3)]">
                    <p>Error al buscar</p>
                    <button
                      className="mt-2 underline"
                      onClick={() => {
                        if (diagramsQuery.isError) void diagramsQuery.refetch();
                      }}
                    >
                      Reintentar
                    </button>
                  </div>
                ) : diagramsQuery.isPending || diagramsQuery.isLoading ? (
                  <p role="status" className="px-4 py-6 text-center text-sm text-[var(--xd-fg-3)]">
                    {diagramsQuery.isPaused
                      ? 'Esperando conexión para buscar...'
                      : 'Cargando resultados...'}
                  </p>
                ) : results.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-[var(--xd-fg-3)]">
                    No se encontraron resultados
                  </p>
                ) : (
                  results.map((diagram) => (
                    <button
                      key={diagram.id}
                      onClick={() => handleSelectResult(diagram.id)}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--xds-gl)]"
                    >
                      <Network className="h-4 w-4 shrink-0 text-[var(--xd-fg-3)]" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[var(--xd-fg)]">
                          {diagram.title || 'Sin título'}{' '}
                          <span className="text-xs text-[var(--xd-fg-3)]">· Diagrama</span>
                        </p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
